import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import programService from '@/shared/services/programService';

const ROLE_ORDER = ['faculty', 'coordinator', 'inbox', 'student'];
const ROLE_HEADINGS = {
    faculty: 'Faculty member and instructor of record',
    coordinator: 'Program coordinators',
    inbox: 'Program contact address',
    student: 'Student',
};
const MODE_LABELS = {
    immediate: 'Send immediately',
    digest: 'Daily digest (sent immediately for now)',
    off: 'Off',
};

// The server's check (server/src/utils/contactAddresses.js), so a typo is
// named before the save; the server checks again
const ADDRESS = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;
const MAX_ADDRESS_LENGTH = 254;
const MAX_CONTACT_ADDRESSES = 10;

/** Why the contact field can't be saved, or null when it can. */
function contactError(value) {
    const addresses = value
        .split(',')
        .map((address) => address.trim())
        .filter(Boolean);
    const bad = addresses.find(
        (address) =>
            address.length > MAX_ADDRESS_LENGTH || !ADDRESS.test(address)
    );
    if (bad) {
        return `"${bad}" is not an email address. Separate addresses with commas.`;
    }
    const distinct = new Set(addresses.map((address) => address.toLowerCase()));
    if (distinct.size > MAX_CONTACT_ADDRESSES) {
        return `Enter at most ${MAX_CONTACT_ADDRESSES} contact addresses.`;
    }
    return null;
}

const cellKey = (event, role) => `${event}|${role}`;

function cellsFromMatrix(matrix) {
    return Object.fromEntries(
        (matrix || []).map((c) => [cellKey(c.event, c.role), c.mode])
    );
}

function ProgramNotificationSettings({ programType }) {
    const [events, setEvents] = useState([]);
    const [contactEmail, setContactEmail] = useState('');
    // What is wrong with the contact field, found before the save
    const [contactProblem, setContactProblem] = useState(null);
    const contactRef = useRef(null);
    const [cells, setCells] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [saved, setSaved] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [{ events: catalog }, config] = await Promise.all([
                programService.getNotificationEvents(),
                programService.getConfig(programType).catch((err) => {
                    // A program with no saved configuration yet has no settings
                    if (err.response?.status === 404) return {};
                    throw err;
                }),
            ]);
            setEvents(catalog);
            setContactEmail(config.contactEmail || '');
            setCells(cellsFromMatrix(config.notificationMatrix));
        } catch (_err) {
            setLoadError('Failed to load notification settings.');
        } finally {
            setLoading(false);
        }
    }, [programType]);

    useEffect(() => {
        load();
    }, [load]);

    const modeFor = (event, role) =>
        cells[cellKey(event.key, role.role)] || role.defaultMode;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const problem = contactError(contactEmail);
        if (problem) {
            setContactProblem(problem);
            setSaved(false);
            contactRef.current?.focus();
            return;
        }
        setSaving(true);
        setSaved(false);
        setSaveError(null);
        const notificationMatrix = events.flatMap((event) =>
            event.roles
                .filter(
                    (role) =>
                        !(event.required || role.required) &&
                        modeFor(event, role) !== role.defaultMode
                )
                .map((role) => ({
                    event: event.key,
                    role: role.role,
                    mode: modeFor(event, role),
                }))
        );
        try {
            const config = await programService.upsertConfig(programType, {
                contactEmail: contactEmail.trim(),
                notificationMatrix,
            });
            setContactEmail(config.contactEmail || '');
            setSaved(true);
        } catch (err) {
            setSaveError(
                err.response?.data?.message ||
                    'Failed to save notification settings.'
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-4" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading notification settings...
            </div>
        );
    }

    if (loadError) {
        return <Alert variant="danger">{loadError}</Alert>;
    }

    const programLabel = programType.toUpperCase();
    const contactId = `notification-contact-${programType}`;

    return (
        <Form onSubmit={handleSubmit} noValidate>
            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <h2 className="h5">Notifications</h2>
                    <Form.Group className="mb-0" controlId={contactId}>
                        <Form.Label>Program contact address</Form.Label>
                        {/* Not `multiple`: browsers rewrite a multiple email
                            value as it is typed (the spaces after commas go),
                            which fights this controlled field. The form checks
                            each address itself. */}
                        <Form.Control
                            type="email"
                            ref={contactRef}
                            placeholder="program@msudenver.edu, help@msudenver.edu"
                            value={contactEmail}
                            onChange={(e) => {
                                setContactEmail(e.target.value);
                                setContactProblem(null);
                            }}
                            isInvalid={!!contactProblem}
                            aria-describedby={
                                contactProblem
                                    ? `${contactId}-error ${contactId}-help`
                                    : `${contactId}-help`
                            }
                        />
                        {contactProblem && (
                            <Form.Control.Feedback
                                type="invalid"
                                id={`${contactId}-error`}
                            >
                                {contactProblem}
                            </Form.Control.Feedback>
                        )}
                        <Form.Text id={`${contactId}-help`} muted>
                            Separate addresses with commas. Faculty who reply to
                            a {programLabel} email reach these addresses, and
                            each also receives the {programLabel} coordinator
                            notices below. With none set, emails come from a
                            no-reply address.
                        </Form.Text>
                    </Form.Group>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-3 overflow-hidden">
                <Card.Body>
                    <Table responsive bordered className="align-middle mb-0">
                        <caption className="text-body-secondary">
                            Email for each event and recipient. In-app
                            notifications are always sent.
                        </caption>
                        <thead>
                            <tr>
                                <th scope="col">Event</th>
                                {ROLE_ORDER.map((role) => (
                                    <th key={role} scope="col">
                                        {ROLE_HEADINGS[role]}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((event) => (
                                <tr key={event.key}>
                                    <th scope="row" className="fw-normal">
                                        {event.label}
                                    </th>
                                    {ROLE_ORDER.map((roleName) => {
                                        const role = event.roles.find(
                                            (r) => r.role === roleName
                                        );
                                        if (!role) {
                                            return (
                                                <td
                                                    key={roleName}
                                                    className="text-body-secondary"
                                                >
                                                    <span aria-hidden="true">
                                                        -
                                                    </span>
                                                    <span className="visually-hidden">
                                                        Not sent to this
                                                        recipient
                                                    </span>
                                                </td>
                                            );
                                        }
                                        if (event.required || role.required) {
                                            return (
                                                <td key={roleName}>
                                                    <strong>Always sent</strong>
                                                    <span className="d-block small text-body-secondary">
                                                        {event.note ||
                                                            'Required notice: the person affected must receive it.'}
                                                    </span>
                                                </td>
                                            );
                                        }
                                        const mode = modeFor(event, role);
                                        return (
                                            <td key={roleName}>
                                                <Form.Select
                                                    size="sm"
                                                    aria-label={`${event.label}: ${role.label}`}
                                                    value={mode}
                                                    onChange={(e) =>
                                                        setCells((prev) => ({
                                                            ...prev,
                                                            [cellKey(
                                                                event.key,
                                                                roleName
                                                            )]: e.target.value,
                                                        }))
                                                    }
                                                >
                                                    <option value="immediate">
                                                        {MODE_LABELS.immediate}
                                                    </option>
                                                    {mode === 'digest' && (
                                                        <option value="digest">
                                                            {MODE_LABELS.digest}
                                                        </option>
                                                    )}
                                                    <option value="off">
                                                        {MODE_LABELS.off}
                                                    </option>
                                                </Form.Select>
                                                {event.note && (
                                                    <span className="d-block small text-body-secondary">
                                                        {event.note}
                                                    </span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {saveError && <Alert variant="danger">{saveError}</Alert>}
            {saved && (
                <Alert variant="success" role="status">
                    Notification settings saved.
                </Alert>
            )}
            <Button type="submit" variant="primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save notification settings'}
            </Button>
        </Form>
    );
}

export default ProgramNotificationSettings;
